import * as React from 'react'

import { observer } from 'mobx-react'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Back } from 'modules/core/components/Card/types'
import CrashLogo from 'modules/core/components/CrashLogo'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import conditions from 'modules/reports/store/collections/conditions'
import dashboards from 'modules/reports/store/collections/dashboards'
import favouriteDashboards from 'modules/reports/store/collections/favourite_dashboards'
import fields from 'modules/reports/store/collections/fields'
import filters from 'modules/reports/store/collections/filters'
import projections from 'modules/reports/store/collections/projections'
import reports from 'modules/reports/store/collections/reports'

export interface RenderProps {
  back: Back
  loading: boolean
}

const ReportsRoot = ({ children }: React.ConsumerProps<RenderProps>) => {
  const { navigation } = useNavigation()

  const [hasFailedFetching, setHasFailedFetching] = React.useState(false)
  const { loading } = useAsyncData(() => {
    const fetchs = [
      dashboards.isEmpty ? dashboards.fetch() : Promise.resolve(),
      favouriteDashboards.isEmpty ? favouriteDashboards.fetch() : Promise.resolve(),
      reports.isEmpty ? reports.fetch() : Promise.resolve(),
      fields.isEmpty ? fields.fetch() : Promise.resolve(),
      projections.isEmpty ? projections.fetch() : Promise.resolve(),
      filters.isEmpty ? filters.fetch() : Promise.resolve(),
      conditions.isEmpty ? conditions.fetch() : Promise.resolve(),
    ]
    return Promise.all(fetchs)
      .then(() =>
        Promise.resolve([
          dashboards,
          favouriteDashboards,
          reports,
          fields,
          projections,
          filters,
          conditions,
        ])
      ) // Promise.all resolved
      .catch(() => {
        setHasFailedFetching(true)
        return Promise.resolve()
      }) // Promise.all has at least one rejection
  }, [])

  const back: Back = {
    href: navigation.reports.fullPath(),
    label: i18n.t('custom_reports.editor.back'),
  }

  if (hasFailedFetching) {
    return <CrashLogo compact />
  }

  return (
    <AuthorizeUser authorizeFn={() => users.current.policy.can('reports.useReports')}>
      {children({
        back,
        loading,
      })}
    </AuthorizeUser>
  )
}

export default observer(ReportsRoot)
